import { useEffect, useState } from 'react';
import { IconCheck } from '@/components/Icons';
import { useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { formatShortDate } from '@/lib/time';
import { ADAPTERS, FALLBACK_MODELS, KEY_PLACEHOLDER, pickDefaultModel, PROVIDER_LABEL, PROVIDER_ORDER } from '@/llm';
import { describeError } from '@/llm/types';
import { describeMarketError } from '@/market/errors';
import { verifyFinnhub } from '@/market/finnhub';
import { verifyMassive } from '@/market/massive';
import { verifyTwelvedata } from '@/market/twelvedata';
import { refreshPriceFeed } from '@/reports/priceFeed';
import { ensureSymbolIndex } from '@/market/symbolIndex';
import { settingsStore, useSettings } from '@/settings/settingsStore';
import s from './keyPanel.module.css';

type Kind = 'llm' | 'finnhub' | 'massive' | 'twelvedata';

/** Key entry + "save & verify" for the LLM provider or one of the two market-data sources. Shared by Setup and Settings. */
export function KeyPanel({ kind, compact = false }: { kind: Kind; compact?: boolean }) {
  const { t } = useT();
  const settings = useSettings();
  const provider = settings.llm.activeProvider;
  const saved = kind === 'llm' ? settings.llm.providers[provider] : settings.data[kind];
  const previousModel = kind === 'llm' ? settings.llm.providers[provider]?.model : undefined;
  const [draft, setDraft] = useState(saved?.apiKey ?? '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setDraft(saved?.apiKey ?? '');
    setResult(null);
  }, [saved?.apiKey, provider, kind]);

  const verify = async () => {
    const key = draft.trim();
    if (!key || busy) return;
    setBusy(true);
    setResult(null);
    try {
      if (kind === 'llm') {
        settingsStore.updateProvider(provider, { apiKey: key });
        try {
          const models = await ADAPTERS[provider].listModels(key);
          settingsStore.updateProvider(provider, { models, modelsFetchedAt: Date.now(), modelsSource: 'live', model: pickDefaultModel(models, previousModel) });
          void refreshPriceFeed(true); // a fresh model list may name models the cached prices predate
          setResult({ ok: true, text: t('keys.verifiedModels', { n: models.length }) });
        } catch (e) {
          const fallback = FALLBACK_MODELS[provider];
          settingsStore.updateProvider(provider, { models: fallback, modelsFetchedAt: Date.now(), modelsSource: 'fallback', model: pickDefaultModel(fallback, previousModel) });
          setResult({ ok: false, text: t('keys.verifyFailedFallback', { error: describeError(e) }) });
        }
      } else {
        settingsStore.setDataKey(kind, { apiKey: key, status: 'unverified' });
        try {
          const sample = kind === 'finnhub' ? await verifyFinnhub(key) : kind === 'massive' ? await verifyMassive(key) : await verifyTwelvedata(key);
          if (kind === 'finnhub') void ensureSymbolIndex('US', key);
          settingsStore.setDataKey(kind, { apiKey: key, status: 'ok', verifiedAt: Date.now(), detail: sample });
          setResult({ ok: true, text: t(kind === 'finnhub' ? 'keys.verifiedFinnhub' : kind === 'massive' ? 'keys.verifiedMassive' : 'keys.verifiedTwelvedata', { sample }) });
        } catch (e) {
          settingsStore.setDataKey(kind, { apiKey: key, status: 'invalid', detail: describeMarketError(e) });
          setResult({ ok: false, text: t('keys.verifyFailed', { error: describeMarketError(e) }) });
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    if (kind === 'llm') settingsStore.clearProvider(provider);
    else settingsStore.clearDataKey(kind);
    setDraft('');
    setResult(null);
  };

  const label = kind === 'llm' ? t('keys.llmKey', { provider: PROVIDER_LABEL[provider] }) : kind === 'finnhub' ? t('keys.finnhubKey') : kind === 'massive' ? t('keys.massiveKey') : t('keys.twelvedataKey');
  const placeholder = kind === 'llm' ? KEY_PLACEHOLDER[provider] : kind === 'finnhub' ? 'c4k9pq…' : kind === 'massive' ? t('keys.massivePlaceholder') : t('keys.twelvedataPlaceholder');
  const llm = kind === 'llm' ? settings.llm.providers[provider] : undefined;
  const dataState = kind !== 'llm' ? settings.data[kind] : undefined;

  const savedStatus = () => {
    if (llm?.apiKey) {
      return llm.modelsSource === 'live'
        ? <span className="up">{t('keys.statusLive', { n: llm.models?.length ?? 0, date: llm.modelsFetchedAt ? formatShortDate(llm.modelsFetchedAt) : '' })}</span>
        : <span className="muted">{t('keys.statusFallback')}</span>;
    }
    if (dataState?.apiKey) {
      if (dataState.status === 'ok') return <span className="up">{t('keys.statusOk', { detail: dataState.detail ?? '' })}</span>;
      if (dataState.status === 'invalid') return <span className="down">{t('keys.statusInvalid', { detail: dataState.detail ?? '' })}</span>;
      return <span className="muted">{t('keys.statusUnverified')}</span>;
    }
    return <span className="muted">{t('keys.statusNone')}</span>;
  };

  return (
    <div className={s.panel}>
      {kind === 'llm' && (
        <div className={s.row}>
          <div className="seg">
            {PROVIDER_ORDER.map((p) => (
              <button key={p} type="button" className={cx(provider === p && 'on')} onClick={() => settingsStore.setActiveProvider(p)}>{PROVIDER_LABEL[p]}</button>
            ))}
          </div>
          {!compact && <span className="muted" style={{ fontSize: 12 }}>{t('keys.providerNote')}</span>}
        </div>
      )}
      <div className={cx(s.grid, kind === 'llm' && s.gridLLM)}>
        <div className={s.field}>
          <span className="label">{label}</span>
          <div className={s.keyRow}>
            <input className="input num" type="password" autoComplete="off" spellCheck={false} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void verify()} />
            <button type="button" className="btn primary" disabled={busy || !draft.trim()} onClick={() => void verify()}>{busy ? t('keys.verifying') : t('keys.verify')}</button>
          </div>
          <div className={s.status}>
            {result ? (
              <span className={cx(result.ok ? 'up' : 'down')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{result.ok && <IconCheck size={13} />}{result.text}</span>
            ) : savedStatus()}
            {(llm?.apiKey || dataState?.apiKey) && <button type="button" className="btn link" style={{ fontSize: 12 }} onClick={clear}>{t('keys.clear')}</button>}
          </div>
        </div>
        {kind === 'llm' && (
          <div className={s.field}>
            <span className="label">{t('keys.model')}</span>
            <select className="input" value={llm?.model ?? ''} disabled={!llm?.models?.length} onChange={(e) => settingsStore.updateProvider(provider, { model: e.target.value })}>
              {!llm?.models?.length && <option value="">{t('keys.modelPlaceholder')}</option>}
              {llm?.models?.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <span className="muted" style={{ fontSize: 12 }}>{t('keys.modelNote')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
